import { FC, useState } from "react"
import { toast } from "react-toastify"

import { useTranslation } from "react-i18next"

import { Toggle } from "components/inputs/Toggle"

import { getLanguageName } from "constants/languages"

import { settingsService } from "services/settings/settings.service"
import { LocaleUrlsConfig } from "services/settings/type"

import { LocaleUrlsSection } from "./LocaleUrlsSection"
import { SettingsHistoryNote } from "./SettingsHistoryNote"
import { SettingsSectionActions } from "./SettingsSectionActions"

interface LanguagesTabProps {
  languages: Record<string, boolean>
  localeUrls: LocaleUrlsConfig | undefined
  onChange: (languages: Record<string, boolean>) => void
  refreshSettings: () => Promise<void>
  onSaved?: () => void
}

export const LanguagesTab: FC<LanguagesTabProps> = ({ languages, localeUrls, onChange, refreshSettings, onSaved }) => {
  const { t } = useTranslation("translation", { useSuspense: false })
  const [saving, setSaving] = useState(false)
  const [historyKey, setHistoryKey] = useState(0)

  // The backend refuses to disable the language the URL structure points at,
  // so the toggle is locked rather than letting the save come back rejected.
  const rootLocale = localeUrls?.default ?? "en"

  const handleToggle = (code: string) => {
    if (code === rootLocale) return

    onChange({ ...languages, [code]: !languages[code] })
  }

  const handleSave = async () => {
    setSaving(true)
    try {
      const success = await settingsService.updateLanguages(languages)
      if (success) {
        toast.success(t("settings.languages.updated") as string)
        onSaved?.()
        setHistoryKey((key) => key + 1)
      }
    } finally {
      setSaving(false)
    }
  }

  const handleResetDone = async () => {
    await refreshSettings()
    setHistoryKey((key) => key + 1)
  }

  return (
    <section className="flex flex-col gap-4">
      <div>
        <h3 className="text-xl font-semibold mb-1 text-[rgba(6,7,21,1)] dark:text-white">{t("settings.languages.title")}</h3>
        <SettingsHistoryNote target="languages" refreshKey={historyKey} />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 rounded-lg border border-black/20 dark:border-white/10 bg-white dark:bg-transparent">
        {Object.entries(languages).map(([code, enabled]) => (
          <div
            key={code}
            className="flex items-center justify-between gap-4 border border-black/10 dark:border-white/10 py-4 px-2 odd:bg-black/[0.03] dark:odd:bg-white/[0.04]"
          >
            <span className="text-base font-semibold text-gray-800 dark:text-white" title={getLanguageName(code)}>
              {code.toUpperCase()}
              <span className="ml-2 text-xs font-normal text-gray-500 dark:text-gray-400">{getLanguageName(code)}</span>
            </span>
            <Toggle
              checked={enabled}
              onChange={() => handleToggle(code)}
              disabled={saving || code === rootLocale}
            />
          </div>
        ))}
      </div>

      <p className="text-xs text-gray-500 dark:text-gray-400">
        {t("settings.languages.rootLocked", { language: rootLocale.toUpperCase() })}
      </p>

      <SettingsSectionActions
        onSave={handleSave}
        saving={saving}
        resetTarget="languages"
        resetLabel={t("settings.tabs.languages")}
        onResetDone={handleResetDone}
      />

      <LocaleUrlsSection languages={languages} localeUrls={localeUrls} refreshSettings={refreshSettings} />
    </section>
  )
}
